/* eslint-disable import/prefer-default-export */
import { isNull, reduce } from 'lodash';

import titleCase from 'common/utils/titleCase';
import graphqlClient from '../graphql';
import {
  addPartnerWithApiAccessGql,
  getPartnerCarrierWebhookConfigGql,
  getPartnerCatalogGql,
  getPartnerOtcCatalogGql,
  getPartnerPracticesGql,
  getPartnerWorkflowConfigGql,
  getPartnersGql,
  listPartnerProfilesGql,
  savePartnerWorkflowConfigGql,
  updatePartnerCarrierWebhookConfigGql,
  updatePartnerCatalogDefaultsGql,
  updatePartnerCatalogGql,
  updatePartnerCatalogStatusGql,
  updatePartnerGql,
} from './gql';

export const getPartnersApi = () => {
  return graphqlClient.request(getPartnersGql).then(({ getPartners }) => getPartners);
};

export const getPartnerPracticesApi = (partnerIds) => {
  return graphqlClient
    .request(getPartnerPracticesGql, { partnerIds })
    .then(({ getPartnerPractices }) => getPartnerPractices);
};

export const listPartnersApi = ({ page, sortBy, filter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);
  const defaultPageOffset = Number(process.env.REACT_APP_DEFAULT_PAGE_OFFSET);

  return graphqlClient
    .request(listPartnerProfilesGql, {
      limit: defaultItemsPerPage,
      offset: (page - 1) * defaultItemsPerPage || defaultPageOffset,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(({ listPartners }) => listPartners);
};

export const getPartnerWorkflowConfigApi = (partnerUUID) => {
  return graphqlClient
    .request(getPartnerWorkflowConfigGql, { partnerUUID })
    .then(({ getLatestPartnerWorkflowConfig }) => getLatestPartnerWorkflowConfig);
};

export const savePartnerWorkflowConfigApi = ({ partnerUUID, input }) => {
  return graphqlClient
    .request(savePartnerWorkflowConfigGql, { partnerUUID, input })
    .then(({ savePartnerWorkflowConfig }) => savePartnerWorkflowConfig);
};

export const getPartnerCarrierWebhookConfigApi = (partnerUuid) => {
  return graphqlClient
    .request(getPartnerCarrierWebhookConfigGql, { partnerUuid })
    .then(({ getPartnerConfig }) => getPartnerConfig?.carrierTrackingWebhookConfig);
};

export const savePartnerCarrierWebhookConfigApi = (input) => {
  return graphqlClient
    .request(updatePartnerCarrierWebhookConfigGql, { input })
    .then(
      ({ updatePartnerCarrierTrackingWebhookConfig }) => updatePartnerCarrierTrackingWebhookConfig
    );
};

export const addPartnerWithApiAccessApi = (input) => {
  return graphqlClient
    .request(addPartnerWithApiAccessGql, { input })
    .then(({ addPartnerWithApiAccess }) => addPartnerWithApiAccess);
};

export const getPartnerCatalogApi = (params) => {
  return graphqlClient
    .request(getPartnerCatalogGql, {
      partnerUuid: params.partnerId,
      ...(params.onlyPartnerEnabled && { onlyPartnerEnabled: params.onlyPartnerEnabled }),
    })
    .then(({ getPartnerCatalog }) =>
      (getPartnerCatalog ?? []).map((drug) => ({
        ...drug,
        ...(drug.drugName ? { drugName: titleCase(drug.drugName) } : {}),
        variants: (drug.variants ?? []).map((variant) => ({
          ...variant,
          ...(variant.strength ? { strength: titleCase(variant.strength) } : {}),
        })),
      }))
    );
};

export const updatePartnerCatalogApi = ({ partnerUuid, partnerCatalog, alertPartners }) => {
  return graphqlClient
    .request(updatePartnerCatalogGql, { input: { partnerUuid, partnerCatalog, alertPartners } })
    .then(({ updatePartnerCatalog }) => updatePartnerCatalog);
};

export const updatePartnerCatalogStatusApi = ({ partnerUuid, catalogId, isEnabled }) => {
  return graphqlClient
    .request(updatePartnerCatalogStatusGql, { input: { partnerUuid, catalogId, isEnabled } })
    .then(({ updatePartnerCatalogStatus }) => updatePartnerCatalogStatus);
};

export const updatePartnerCatalogDefaultsApi = (params) => {
  return graphqlClient
    .request(updatePartnerCatalogDefaultsGql, {
      input: {
        partnerUuid: params.partnerUuid,
        newProductsActiveByDefault: params.newProductsActiveByDefault,
      },
    })
    .then(({ updatePartnerCatalogDefaults }) => updatePartnerCatalogDefaults);
};

export const updatePartnerApi = ({ partnerUuid, data }) => {
  return graphqlClient
    .request(updatePartnerGql, { partnerUuid, data })
    .then(({ updatePartner }) => updatePartner);
};

export const getPartnerOtcCatalogApi = async ({ partnerUuid }) => {
  return graphqlClient
    .request(getPartnerOtcCatalogGql, { partnerUuid })
    .then(({ getPartnerOtcCatalog }) =>
      (getPartnerOtcCatalog ?? []).map((item) => ({
        ...item,
        ...(item.name ? { name: titleCase(item.name) } : {}),
        ...(item.strength ? { strength: titleCase(item.strength) } : {}),
      }))
    );
};
